import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../lib/utils';
import { usePageContext, generateTrackingId, pushGTMEvent } from '../../utils/tracking';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  // Tracking props
  trackingId?: string;
  trackingSection?: string;
  disableTracking?: boolean;
}

export function Modal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md', 
  className,
  trackingId,
  trackingSection,
  disableTracking = false 
}: ModalProps) {
  const { pageName, section } = usePageContext();
  
  // Generate tracking ID if not provided and tracking is enabled
  const autoTrackingId = !disableTracking && !trackingId ? 
    generateTrackingId({
      elementType: 'modal',
      pageName,
      elementText: title,
      section: trackingSection || section,
      variant: size
    }) : trackingId;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      
      // Track modal open event
      if (!disableTracking && autoTrackingId) {
        pushGTMEvent({
          elementType: 'modal',
          elementId: autoTrackingId,
          elementText: title,
          pageName,
          section: trackingSection || section,
          variant: size,
          userAction: 'view'
        });
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, autoTrackingId, disableTracking, title, pageName, section, trackingSection, size]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleClose = () => {
    // Track modal close event
    if (!disableTracking && autoTrackingId) {
      pushGTMEvent({
        elementType: 'modal',
        elementId: autoTrackingId,
        elementText: title,
        pageName,
        section: trackingSection || section,
        variant: size,
        userAction: 'click'
      });
    }
    
    onClose();
  };

  const handleBackdropClick = () => {
    // Track backdrop close event
    if (!disableTracking && autoTrackingId) {
      pushGTMEvent({
        elementType: 'modal',
        elementId: `${autoTrackingId}-backdrop`,
        elementText: 'backdrop',
        pageName,
        section: trackingSection || section,
        variant: size,
        userAction: 'click'
      });
    }
    
    onClose();
  };

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div 
        className={cn(
          'relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full mx-4 max-h-[90vh] overflow-hidden transition-colors',
          sizeClasses[size],
          className
        )}
        id={autoTrackingId}
        data-tracking-id={autoTrackingId}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleClose}
              trackingId={`${autoTrackingId}-close-button`}
              trackingSection={trackingSection || section}
              disableTracking={disableTracking}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        )}
        
        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {children}
        </div>
      </div>
    </div>
  );
}